import React, { useEffect, useState } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import { Permission } from '../../core/auth0'

import { signageBannersActions } from '../../core/signage-banners'
import { modalActions } from '../../core/modal'
import { brandsActions } from '../../core/brands'
import RootState from '../../core/root-state'
import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import { SignageBanner } from '../../core/signage-banners'
import { Brand } from '../../core/brands'
import { strings } from '../components/signage-banners/signage-banners-content'
import styles from '../styles/modules/signage-banners/signage-banner-items.module.scss'
import SignageBannerTale from '../components/signage-banners/signage-banner-table'
import BrandFilter from '../components/common/brand-filter'
import { BrandCode, BRAND_CODES } from '../../core/brands'
import { roleType } from '../../core/auth0'

interface Props {
  signageBanners: [SignageBanner]
  isLoading: boolean
  role: string
  brands: Brand[]
  fetchSignageBanners: (brandCode?: string) => void
  fetchBrands: () => void
  showCreateSignageBanner: () => void
  deleteSignageBanner: (id: number) => void
  sortSignageBanners: (ids: number[]) => void
  updateSignageBanners: (id: number, duration: number, isShow: boolean) => void
  showEditSignageBannerDuration: (
    id: number,
    duration: number,
    isShow: boolean
  ) => void
}

const SignageBannerScreen: React.FC<Props> = ({
  signageBanners,
  isLoading,
  role,
  brands,
  fetchSignageBanners,
  fetchBrands,
  showCreateSignageBanner,
  deleteSignageBanner,
  sortSignageBanners,
  updateSignageBanners,
  showEditSignageBannerDuration,
}) => {
  const [items, setItems] = useState<SignageBanner[]>([])
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(null)
  const [filterTriggered, setFilterTriggered] = useState(false)
  const isAdmin = role === roleType.ADMIN

  useEffect(() => {
    // adminユーザーは初期選択をrootcに設定し、rootcのデータを表示
    if (isAdmin) {
      setSelectedBrand(BRAND_CODES.ROOTC)
      fetchSignageBanners(BRAND_CODES.ROOTC)
    } else {
      // 非adminユーザーは従来通り
      fetchSignageBanners()
    }
    fetchBrands()
    // 初期化処理のため、コンポーネントマウント時に一度だけ実行される必要がある
    // 依存配列に関数を追加すると、フィルタリング後に再度この処理が実行されてしまう
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleBrandChange = (brand: BrandCode) => {
    setSelectedBrand(brand)
  }

  const handleFilterClick = () => {
    setFilterTriggered(true)
    fetchSignageBanners(selectedBrand)
  }

  useEffect(() => {
    if (filterTriggered && !isLoading) {
      setFilterTriggered(false)
    }
  }, [filterTriggered, isLoading])

  const handleOnClickCreateSignageBanner = () => {
    showCreateSignageBanner()
  }

  useEffect(() => {
    setItems(signageBanners)
  }, [signageBanners])

  const handleOnClickDeleteSignageBanner = (id: number) => {
    const response = window.confirm('本当に削除しますか？')
    if (response) {
      deleteSignageBanner(id)
    }
  }

  const handleOnClickEditSignageBannerDuration = (
    id: number,
    duration: number,
    isShow: boolean
  ) => {
    showEditSignageBannerDuration(id, duration, isShow)
  }

  return (
    <Layout location={'signage_banner'}>
      <Wrapper title={strings.title}>
        <div className={styles.filterContainer}>
          <BrandFilter
            currentBrand={selectedBrand}
            onBrandChange={handleBrandChange}
            onFilterClick={handleFilterClick}
            isAdminOnly={isAdmin}
            brands={brands}
            isLoading={isLoading && filterTriggered}
          />
        </div>
        <div className={styles.container}>
          <SignageBannerTale
            signageBanners={items}
            setSignageBanners={setItems}
            sortSignageBanners={sortSignageBanners}
            updateSignageBanners={updateSignageBanners}
            handleOnClickEditSignageBannerDuration={
              handleOnClickEditSignageBannerDuration
            }
            onClickCreateSignageBanner={handleOnClickCreateSignageBanner}
            onClickDeleteSignageBanner={handleOnClickDeleteSignageBanner}
            showEditSignageBannerDuration={showEditSignageBannerDuration}
          />
        </div>
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  signageBanners: state.signageBanners.signageBanners,
  isLoading: state.signageBanners.isLoading,
  role: state.auth0.role,
  brands: state.brands.brands,
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  fetchSignageBanners: (brandCode?: string) =>
    dispatch(signageBannersActions.fetchSignageBanners(brandCode)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
  showCreateSignageBanner: () =>
    dispatch(modalActions.showCreateSignageBanner()),
  deleteSignageBanner: (id: number) =>
    dispatch(signageBannersActions.deleteSignageBanner(id)),
  sortSignageBanners: (ids: number[]) =>
    dispatch(signageBannersActions.sortSignageBanners(ids)),
  updateSignageBanners: (id: number, duration: number, isShow: boolean) =>
    dispatch(signageBannersActions.updateSignageBanners(id, duration, isShow)),
  showEditSignageBannerDuration: (
    id: number,
    duration: number,
    isShow: boolean
  ) =>
    dispatch(modalActions.showEditSignageBannerDuration(id, duration, isShow)),
})

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(SignageBannerScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.SIGNAGE_BANNER_READ,
  redirectTo: '/404',
})
